import { datadogRum } from "@datadog/browser-rum";
import { keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { FeatureFlags } from "@utils/FeatureFlags/generated-flags";
import { computeIntegratedButMissingLink, decideClientListImportAction, isOrgEhrUsable } from "@utils/ehrActions";
import { unwrapConnection } from "@utils/utils";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Dialog, Paragraph, Select, SelectItem, Small } from "@fronterahealth/frontera-ui-components";

import {
  ApiReportTemplateReportTypeChoices,
  EhrConnectionStatus,
  ReportTypeEnums,
  useCreateReportMutation,
  useGetProviderLearnersQuery,
  useGetReportsQuery,
} from "@api/graphql/types-and-hooks";
import { computeEhrUserFlags } from "@components/ClinicalNewReportDialog/ClinicalNewReportDialog";
import { EHRLoginDialog } from "@components/ClinicalNewReportDialog/EHRLoginDialog";
import { Loader } from "@components/ClinicalNewReportDialog/Loader";
import { EhrSyncGatedNotice } from "@components/EHRConnect/EhrSyncGatedNotice";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useClientListImport } from "@hooks/useClientListImport";
import { useReportTemplateSelection } from "@hooks/useReportTemplateSelection";
import { useAdminData } from "@providers/AdminDataProvider";
import { useEHRClientData } from "@providers/EHRClientProvider";
import { useEHRConnection } from "@providers/EHRConnectionProvider";

interface NewAssessmentDialogProps {
  setDialogOpen: (open: boolean) => void;
  isDialogOpen: boolean;
  credits: number;
  isSelfServeUser: boolean;
}

export const NewAssessmentDialog: React.FC<NewAssessmentDialogProps> = ({
  isDialogOpen,
  setDialogOpen,
  credits,
  isSelfServeUser,
}) => {
  const [learnerName, setLearnerName] = useState<string>("");
  const [selectedClient, setSelectedClient] = useState<SelectItem | undefined>();
  /** Snapshot of selected learner when chosen from filtered list; used for EHR validation after search clears and refetched list may not include them (e.g. pagination). */
  const [selectedLearnerSnapshot, setSelectedLearnerSnapshot] = useState<{
    id: string;
    externalId?: string | null;
  } | null>(null);
  const [isEHRLogin, setIsEHRLogin] = useState<boolean>(false);
  const flags = useFlags<FeatureFlags>();
  const { templateSelections, templatesLoading, selectedTemplate, setSelectedTemplate } = useReportTemplateSelection(
    ApiReportTemplateReportTypeChoices.InitialAssessment,
    "get-report-templates-initial-assessment",
    selectedClient?.id,
  );

  const go = useNavigate();
  const {
    isPassageHealthUser,
    isFronteraHealthTypeOrg,
    isHiRasmusUser,
    isMotivityUser,
    isCentralReachUser,
    isHippHealthUser,
    isRethinkUser,
    isEnsoraUser,
    externalOrganizationName,
    isDataWarehouseOrg,
    isOrgUsernamePasswordOrg,
    isEhrSyncCleared,
  } = useAdminData();
  const reportType = ReportTypeEnums.InitialAssessment;
  const { setIsClientImportedFromEHR } = useEHRClientData();
  const { hasCredentials, usesOrgSession } = useEHRConnection();
  const queryClient = useQueryClient();

  const { isEhrIntegrationUser, isCRDataWarehouse, isOrgUsernamePassword, isApiBasedEhrUser } = computeEhrUserFlags({
    isHiRasmusUser,
    isMotivityUser,
    isCentralReachUser,
    isEnsoraUser,
    isRethinkUser,
    isHippHealthUser,
    isPassageHealthUser,
    isDataWarehouseOrg,
    isOrgUsernamePasswordOrg,
    hippHealthSync: !!flags["hipphealth-sync"],
  });

  const { isImporting, startImport, cancelImport, ehrConnectionStatus } = useClientListImport({
    // INT-36: CentralReach orgs on shared credentials queue the import
    // on the org-wide session instead of signalling a per-provider
    // workflow, and completion is read off the request row.
    usesOrgSession,
    enabled: isEhrIntegrationUser && isDialogOpen,
    waitForStatusTransition: !isApiBasedEhrUser,
    isEhrSyncCleared,
    onSuccess: () => {
      refetchLearners().catch((error) => {
        console.error("Error refetching learners after client import", error);
      });
    },
    onLoginFailed: () => {
      // Org-level admin account: no per-provider login dialog to surface. Refresh
      // the connection so the credential failure shows on the top EHR badge (the
      // hook suppresses its error toast when an onLoginFailed handler is set),
      // matching ClientDashboard. Other orgs open the per-provider login dialog.
      if (isOrgUsernamePassword) {
        queryClient.invalidateQueries({ queryKey: ["ehrConnection"] });
      } else {
        setIsEHRLogin(true);
      }
    },
  });

  const {
    data: learnersQueryData,
    isLoading: learnersLoading,
    refetch: refetchLearners,
  } = useGetProviderLearnersQuery(
    { learnerName },
    {
      queryKey: ["get-provider-learners", learnerName],
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      placeholderData: keepPreviousData,
    },
  );

  useEffect(() => {
    if (!isDialogOpen) {
      setSelectedClient(undefined);
      setSelectedLearnerSnapshot(null);
      setLearnerName("");
      cancelImport();
    }
  }, [isDialogOpen, cancelImport]);

  const {
    data: learnerAssessmentData,
    isLoading: learnerAssessmentLoading,
    refetch: refetchReports,
  } = useGetReportsQuery(
    {
      filter: {
        learnerId: selectedClient?.id,
        reportType,
      },
    },
    {
      queryKey: ["get-learner-assessment", selectedClient?.id],
      enabled: false,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
  );

  const { data: createAssessmentData, isPending, error, mutateAsync: createReport } = useCreateReportMutation({});

  const learners = useMemo(() => unwrapConnection(learnersQueryData?.getProviderLearners) || [], [learnersQueryData]);
  const [learnersById, learnerSelectItems] = useMemo(() => {
    const byId = Object.fromEntries(learners.map((item) => [item.id, item]));
    const selectItems: SelectItem[] = learners
      .map((learner) => ({
        primary: `${learner.learnermetadata?.firstName} ${learner.learnermetadata?.lastName}`,
        id: learner.id,
        className: "ph-no-capture",
      }))
      .sort((la, lb) => (la.primary > lb.primary ? 1 : -1));
    return [byId, selectItems];
  }, [learners]);
  const selectedLearnerFromList = selectedClient && learnersById[selectedClient.id!];
  const selectedLearner =
    selectedLearnerFromList ??
    (selectedClient && selectedLearnerSnapshot?.id === selectedClient.id ? selectedLearnerSnapshot : null);

  const createAssessmentObject = async () => {
    const response = await createReport({
      reportType,
      learnerId: selectedClient?.id,
      reportTemplateId: selectedTemplate?.id,
    });

    const base64DecodedId = atob(response.createReport?.report?.id || "");
    const databaseId = base64DecodedId.split("ReportType:")?.pop();
    datadogRum.addAction("report-creation", {
      reportId: databaseId,
      reportType: reportType,
    });
  };

  useEffect(() => {
    if (error) {
      console.error("Error while creating assessment", error);
      notifyError("Error creating assessment");
    }
  }, [error]);

  useEffect(() => {
    if (!createAssessmentData) return;

    const assessment = createAssessmentData.createReport;
    const assessmentStatus = assessment?.status;

    if (assessmentStatus) {
      notifySuccess(assessment?.message || "Assessment created successfully");

      const learnerAssessment = assessment?.report;

      if (learnerAssessment) {
        const { clientFirstName, clientLastName } = learnerAssessment?.reportClientDetails || {};

        setDialogOpen(false);

        go(`/assessment-reports/${learnerAssessment?.id}`, {
          state: {
            id: learnerAssessment?.id,
            clientName: `${clientFirstName || "N/A"} ${clientLastName || ""}`,
            lastUpdated: new Date(learnerAssessment?.lastUpdatedDate) || new Date(),
          },
        });
      }
    } else {
      notifyError(assessment?.message || "Error creating assessment");
    }
  }, [createAssessmentData]);

  const handleSecondaryButtonClick = () => {
    setDialogOpen(false);
  };

  useEffect(() => {
    const validateObject = async () => {
      await refetchReports();
    };

    if (selectedClient) {
      validateObject();
    }
  }, [selectedClient]);

  const goToReport = () => {
    setDialogOpen(false);
    go(`/assessment-reports/${learnerAssessmentData?.getReports?.edges[0]?.node?.id}`, {
      state: {
        id: learnerAssessmentData?.getReports?.edges[0]?.node?.id,
        clientName: selectedClient?.primary,
        lastUpdated: new Date(learnerAssessmentData?.getReports?.edges[0]?.node?.lastUpdatedDate),
      },
    });
  };

  const checkLoginStateBeforeClientListImport = () => {
    const action = decideClientListImportAction({
      isApiKeyOrg: isHippHealthUser || isPassageHealthUser,
      isEhrIntegrationUser,
      isOrgUsernamePassword,
      hasCredentials,
      ehrConnectionStatus,
      isEhrSyncCleared,
    });
    if (action === "import") {
      startImport();
    } else if (action === "open-login") {
      setIsEHRLogin(true);
    }
  };

  const continueToClientListImportAfterLogin = async (loginSuccess: boolean) => {
    setIsEHRLogin(false);

    if (!isEhrIntegrationUser || !loginSuccess) {
      return;
    }

    await startImport();
  };

  const integratedButMissingLink = computeIntegratedButMissingLink({
    hasSelectedLearner: !!selectedLearner,
    isIntegratedUser: isEhrIntegrationUser,
    hasExternalId: !!selectedLearner?.externalId,
    allowCreateClientWithEhr: !!flags["allow-create-client-with-ehr"],
  });

  return (
    <Dialog
      title="Create Assessment Report"
      open={isDialogOpen}
      setOpen={setDialogOpen}
      primaryButton={{
        text: isPending ? "Creating..." : "Yes, Continue",
        onClick: () => {
          if (isEhrIntegrationUser) {
            setIsClientImportedFromEHR(true);
          }
          createAssessmentObject();
        },
        disabled:
          !!(selectedClient && learnerAssessmentData?.getReports?.edges[0]?.node) || // assessment exists for client
          !selectedTemplate || // template required
          (isPassageHealthUser && !selectedClient) || // passage health but no client
          integratedButMissingLink, // integrated orgs require an external ID
      }}
      secondaryButton={{ text: "No, Cancel", onClick: handleSecondaryButtonClick }}
      size="xl"
    >
      {isSelfServeUser && <SelfServeText credits={credits} />}
      {!isSelfServeUser && (
        <div>
          <Paragraph className="mt-2">
            Create a new initial assessment report for a client. You can start from a base report template or select an
            assessment template set up by your organization.
          </Paragraph>
          <br />
          {isEhrIntegrationUser && (
            <>
              <Paragraph>
                If you don’t see the client you’re looking for, your client list may be out of date.
              </Paragraph>
              {isImporting ? (
                <div className="flex flex-row gap-2 mt-4 items-center">
                  <Loader className="h-5 w-5 ml-0 mr-0" />
                  <Paragraph>Refreshing your client list...</Paragraph>
                </div>
              ) : (
                <Button
                  text="Refresh Client List"
                  appearance="text"
                  onClick={checkLoginStateBeforeClientListImport}
                  className="-ml-3.5 mb-5 disabled:text-limestone-400 disabled:opacity-100"
                  disabled={
                    !isEhrSyncCleared ||
                    (isCRDataWarehouse && ehrConnectionStatus !== EhrConnectionStatus.Connected) ||
                    // Org-admin: only disable when the org EHR is genuinely not set up;
                    // connected, active/in-progress, and CONNECTION_ERROR all allow a retry.
                    (isOrgUsernamePassword && !isOrgEhrUsable(ehrConnectionStatus))
                  }
                />
              )}
              {!isEhrSyncCleared && <EhrSyncGatedNotice />}
              <br />
            </>
          )}
          <Select
            title="Select Client"
            isLoading={learnersLoading}
            items={learnerSelectItems}
            placeholderText="Type to find a client"
            selected={selectedClient}
            setSelected={(val) => {
              setSelectedClient(val);
              if (val?.id) {
                const learner = learnersById[val.id];
                setSelectedLearnerSnapshot(learner ? { id: learner.id, externalId: learner.externalId } : null);
                setLearnerName("");
              } else {
                setSelectedLearnerSnapshot(null);
              }
            }}
            searchValue={learnerName}
            onSearch={(inputValue: string) => setLearnerName(inputValue)}
            searchDebounceMs={400}
            actionItem={
              isFronteraHealthTypeOrg
                ? {
                    label: "Create New",
                    onClick: () => createAssessmentObject(),
                  }
                : undefined
            }
            enableSearch
            errorMessage={
              integratedButMissingLink ? `Client must first be linked to ${externalOrganizationName}` : undefined
            }
          />
          <Select
            title="Select Template"
            isLoading={templatesLoading}
            items={templateSelections}
            placeholderText={
              templateSelections.length > 1 && !selectedTemplate ? "Select a template..." : "Type to find a template"
            }
            selected={selectedTemplate}
            setSelected={setSelectedTemplate}
            enableSearch
          />
        </div>
      )}
      {learnerAssessmentLoading && <Small>Loading...</Small>}
      {learnerAssessmentData?.getReports?.edges[0]?.node ? (
        <>
          <Small displayType="normal" className="-mt-6">
            An assessment for this client already exists.
            <br /> Click{" "}
            <span className="cursor-pointer text-secondary underline" onClick={() => goToReport()}>
              here
            </span>{" "}
            to view
          </Small>
        </>
      ) : (
        <></>
      )}
      {isEHRLogin ? (
        <EHRLoginDialog
          open={isEHRLogin}
          setOpen={setIsEHRLogin}
          setSuccessLogin={continueToClientListImportAfterLogin}
        />
      ) : null}
    </Dialog>
  );
};

const SelfServeText = ({ credits }: { credits: number }) => {
  return (
    <div className="text-text-primary">
      <p>You are about to create a new assessment report.</p>
      <p className="font-semibold">This will use one (1) credit.</p>
      <p className="text-limestone-600 mb-4">You will have {credits - 1} credits remaining.</p>
      <p>Would you like to proceed?</p>
    </div>
  );
};
